import { MinMaxForecast, SkuData } from '../../../../../redistributionTypes';
import React, { useMemo } from 'react';
import SkuInfo, { SkuInfoData } from './SkuInfo';
import { Row, Text } from '@ydistri/ds';
import { getForecastApproachLabel } from '@ydistri/utils';
import CountableText from '../../../../../../../../components/global/CountableText/CountableText';
import NoData, {
  NO_DATA_PLACEHOLDER,
} from '../../../../../../../../components/global/NoData/NoData';
import SkuInfoTypeConfiguration from '../SkuInfo/SkuInfoDetails/SkuInfoTypeConfiguration';
import SkuInfoTypeValue from '../SkuInfo/SkuInfoDetails/SkuInfoTypeValue';
import { forecastTypesDefault } from '../../../../../../../../lib/charts/saleChartsLib';
import { formatDate, fullMonthDiff } from '@ydistri/generic-utils';
import ChartLineLegendGraphic from '../../../../../../../../components/global/SalesChartTooltip/ChartLineLegendGraphic';

interface SkuInfoSourceProps {
  skuData?: SkuData;
  applicationDate: Date;
  usedForecast?: MinMaxForecast;
  showActualData?: boolean;
  hideSkuInfo?: boolean;
}

const getMonthsSinceFirstShipmentValue = (sku: SkuData, applicationDate: Date) => {
  if (!sku.attributes.dates.dateOfFirstPurchase) {
    return 'No shipment info';
  }

  return (
    <Row $gap="0.5rem">
      <CountableText
        value={fullMonthDiff(sku.attributes.dates.dateOfFirstPurchase, applicationDate)}
        word="month"
        highlighting="both"
        suffix="from first shipment"
      />
      <Text>(First shipment: {formatDate(sku.attributes.dates.dateOfFirstPurchase)})</Text>
    </Row>
  );
};

const getMonthsSinceLastShipmentValue = (sku: SkuData, applicationDate: Date) => {
  if (!sku.attributes.dates.dateOfLastPurchase) {
    return 'No shipment info';
  }

  return (
    <Row $gap="0.5rem">
      <CountableText
        value={fullMonthDiff(sku.attributes.dates.dateOfLastPurchase, applicationDate)}
        word="month"
        highlighting="both"
        suffix="from last shipment to store"
      />
      <Text>(Last shipment: {formatDate(sku.attributes.dates.dateOfLastPurchase)})</Text>
    </Row>
  );
};

const getMuToRedistribute = (skuData: SkuData, usedForecastMax?: number) => {
  if (skuData.attributes.values.AvailableSupply && usedForecastMax) {
    const availableSupply = skuData.attributes.values.AvailableSupply
      ? parseFloat(skuData.attributes.values.AvailableSupply)
      : 0;

    const toRedistribute = availableSupply - usedForecastMax;

    return `(${toRedistribute < 0 ? 0 : toRedistribute} MU to redistribute)`;
  }
};

const SkuInfoSource: React.FC<SkuInfoSourceProps> = ({
  skuData,
  applicationDate,
  usedForecast,
  showActualData = false,
  hideSkuInfo = false,
}) => {
  const tableData = useMemo(() => {
    const result: SkuInfoData[] = [];

    if (hideSkuInfo) {
      return result;
    }

    result.push({
      label: skuData?.type ?? 'Type',
      configuration: <SkuInfoTypeConfiguration sku={skuData} />,
      value: skuData ? (
        <SkuInfoTypeValue sku={skuData} applicationDate={applicationDate} />
      ) : (
        <NoData />
      ),
    });

    result.push({
      label: 'Months of supply after redistribution',
      configuration: skuData ? (
        `${skuData.config.PsMonthsOfSupplyToKeep ?? NO_DATA_PLACEHOLDER} months`
      ) : (
        <NoData />
      ),
      value: (
        <Row $gap="0.5rem">
          <Text $semiBold>{`${usedForecast?.max ?? NO_DATA_PLACEHOLDER} MU to keep`}</Text>{' '}
          {skuData && <Text>{getMuToRedistribute(skuData, usedForecast?.max)}</Text>}
        </Row>
      ),
    });

    result.push({
      label: 'Months since 1st shipment to store',
      configuration: skuData?.config.PsMonthsFromFirstPurchase ? (
        <CountableText
          value={parseInt(skuData.config.PsMonthsFromFirstPurchase)}
          word="month"
          highlighting="none"
        />
      ) : (
        NO_DATA_PLACEHOLDER
      ),
      value: skuData ? getMonthsSinceFirstShipmentValue(skuData, applicationDate) : <NoData />,
    });

    result.push({
      label: 'Months since last shipment to store',
      configuration: skuData?.config.PsMonthsFromLastPurchase ? (
        <CountableText
          value={parseInt(skuData.config.PsMonthsFromLastPurchase)}
          word="month"
          highlighting="none"
        />
      ) : (
        NO_DATA_PLACEHOLDER
      ),
      value: skuData ? getMonthsSinceLastShipmentValue(skuData, applicationDate) : <NoData />,
    });

    const minLayers = skuData?.sourceMinLayerLists ?? [];

    result.push({
      label: 'Minimum layers',
      configuration: minLayers.length > 0 ? 'Yes' : 'No',
      value: ' - ',
    });

    const fctMin = forecastTypesDefault.find(ft => ft.priority === 1);
    const fctMax = forecastTypesDefault.find(ft => ft.priority === 2);
    const fctMinLine = fctMin && 'lineInfo' in fctMin ? fctMin.lineInfo : undefined;
    const fctMaxLine = fctMax && 'lineInfo' in fctMax ? fctMax.lineInfo : undefined;

    result.push({
      label: 'Forecasting approach',
      configuration: (
        <>
          <Row $alignItems="center" $gap="0.5rem">
            {fctMaxLine ? <ChartLineLegendGraphic $lineInfo={fctMaxLine} /> : null}
            <Text $bold>Source</Text>
          </Row>
          <Row $alignItems="center" $gap="0.5rem">
            {fctMinLine ? <ChartLineLegendGraphic $lineInfo={fctMinLine} /> : null}
            <Text>Target</Text>
          </Row>
        </>
      ),
      value: (
        <>
          <Text $bold>{getForecastApproachLabel(skuData?.config.PsMaxForecastConfidence)}</Text>
          <br />
          <Text>{getForecastApproachLabel(skuData?.config.PtMinForecastConfidence)}</Text>
        </>
      ),
    });

    return result;
  }, [applicationDate, hideSkuInfo, skuData, usedForecast?.max]);

  return (
    <SkuInfo
      data={tableData}
      showActualData={showActualData}
      skuClass={skuData?.skuClass}
      skuDescription={skuData?.description}
      minLayerLists={skuData?.sourceMinLayerLists}
    />
  );
};

export default SkuInfoSource;
